import { CancellableSearchBox } from '@plato/ui-legacy/components/search/cancellable-search-box';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
} from '@plato/ui/components/select';
import { formatISO } from 'date-fns';
import { ClockIcon, LayersIcon, SquareCheckBigIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PresetDatePicker } from '../../../components/ui/preset-date-picker.tsx';
import { BillingStatus, TargetUnit, mapToTargetUnit } from '../../../server/utils/explorer-utils.ts';
import { getSearchStringFieldForTab, useExplorerView } from '../hooks/use-explorer-query-params.ts';
import { MetricInfoHint } from './metric-explainer.tsx';

type TargetUnitOption = { value: TargetUnit; label: string };
type BillingStatusOption = { value: BillingStatus; label: string };
type YearPeriodOption = { value: string; label: string };

export function ExplorerFilters() {
    const { t } = useTranslation('explorer');

    const { state, setState } = useExplorerView();

    const {
        searchString,
        tab: selectedTab,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange = null,
        billing: selectedBillingStatus,
        sorting: selectedSortingFilter,
        targetUnit: selectedTargetUnit,
        yearPeriods: selectedYearPeriod,
    } = state;

    const targetUnits: TargetUnitOption[] = [
        { value: TargetUnit.REVENUE, label: t('filter_dropdown_unit__monetary') },
        { value: TargetUnit.UNITS, label: t('filter_dropdown_unit__units') },
    ];

    const billingStatuses: BillingStatusOption[] = [
        { value: BillingStatus.BILLED, label: t('filter_dropdown_status__billed') },
        {
            value: BillingStatus.BILLED_WO_CREDIT_NOTE,
            label: t('filter_dropdown_status__billed_wo_credit_note'),
        },
        { value: BillingStatus.NOT_BILLED, label: t('filter_dropdown_status__unbilled') },
        { value: BillingStatus.ORDER_SUM, label: t('filter_dropdown_status__order_sum') },
    ];

    const yearPeriods: YearPeriodOption[] = [2, 3, 4, 5].map((years) => ({
        value: String(years),
        label: t('filter_dropdown__period', { years }),
    }));

    const setSelectedTargetUnit = (unit: TargetUnit) => {
        setState((prevState) => ({
            ...prevState,
            targetUnit: unit,
            sorting: {
                direction: selectedSortingFilter.direction,
                field: mapToTargetUnit[unit][selectedSortingFilter.field],
            },
        }));
    };
    return (
        // The row scrolls sideways, so it needs an outer box a column flex
        // parent cannot squash — the anchor this replaces used to be that box.
        <div className="flex">
            <div className="flex flex-row gap-2 scrollbar-none overflow-auto">
                <CancellableSearchBox
                    key={selectedTab}
                    placeholder={t('search_placeholder__' + selectedTab)}
                    value={searchString}
                    onChange={(value) => {
                        const tabSearchField = getSearchStringFieldForTab(selectedTab);
                        setState((prevState) => ({
                            ...prevState,
                            [tabSearchField]: value,
                        }));
                    }}
                    className="w-[210px] shrink-0"
                />

                <div className="flex items-center gap-tight">
                    <Select
                        title={t('filter_dropdown_unit__label')}
                        items={targetUnits}
                        value={selectedTargetUnit}
                        onValueChange={(unit) => {
                            if (unit) {
                                setSelectedTargetUnit(unit);
                            }
                        }}
                    >
                        <SelectTrigger>
                            <LayersIcon className="size-4 shrink-0 text-content-weaker" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectList>
                                {(unit: TargetUnitOption) => <SelectItem key={unit.value} value={unit} />}
                            </SelectList>
                        </SelectContent>
                    </Select>

                    {/* Only Menge needs a hint; what a monetary value counts is the next select's job. */}
                    {selectedTargetUnit === TargetUnit.UNITS && <MetricInfoHint metric="UNITS" />}
                </div>

                <div className="flex items-center gap-tight">
                    <Select
                        title={t('filter_dropdown_status__label')}
                        items={billingStatuses}
                        value={selectedBillingStatus}
                        onValueChange={(status) => {
                            if (status) {
                                setState((prevState) => ({
                                    ...prevState,
                                    billing: status,
                                }));
                            }
                        }}
                    >
                        <SelectTrigger>
                            <SquareCheckBigIcon className="size-4 shrink-0 text-content-weaker" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectList>
                                {(status: BillingStatusOption) => (
                                    <SelectItem key={status.value} value={status} />
                                )}
                            </SelectList>
                        </SelectContent>
                    </Select>

                    <MetricInfoHint metric={selectedBillingStatus} />
                </div>

                <div className="flex shrink-0">
                    <PresetDatePicker
                        value={{
                            preset: selectedWindow,
                            dateRange: selectedCustomDateRange
                                ? {
                                      from: new Date(selectedCustomDateRange.from),
                                      to: new Date(selectedCustomDateRange.to),
                                  }
                                : null,
                        }}
                        onChange={({ preset, dateRange }) => {
                            setState((prevState) => ({
                                ...prevState,
                                window: preset,
                                customDateRange: dateRange
                                    ? {
                                          from: formatISO(dateRange.from, { representation: 'date' }),
                                          to: formatISO(dateRange.to, { representation: 'date' }),
                                      }
                                    : undefined,
                            }));
                        }}
                    />
                </div>

                <div className="flex">
                    <Select
                        title={t('filter_dropdown__period_label')}
                        items={yearPeriods}
                        value={String(selectedYearPeriod)}
                        onValueChange={(period) => {
                            setState((prevState) => ({
                                ...prevState,
                                yearPeriods: period ? Number.parseInt(period, 10) : 2,
                            }));
                        }}
                    >
                        <SelectTrigger>
                            <ClockIcon className="size-4 shrink-0 text-content-weaker" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectList>
                                {(period: YearPeriodOption) => (
                                    <SelectItem key={period.value} value={period} />
                                )}
                            </SelectList>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
}
